import { Briefcase, FileText, HelpCircle, Home, Settings, User, CreditCard, ExternalLink, Sparkles } from 'lucide-react';

export type PrimaryNavKey = 'home' | 'reports' | 'enhancement' | 'billing' | 'help' | 'workplace' | 'profile';

export interface PrimaryNavItem {
  key: PrimaryNavKey;
  label: string;
  href: string;
  icon: typeof Home;
  matchers: string[];
  external?: boolean;
}

export const getPrimaryNavItems = (lowTokens: boolean = false): PrimaryNavItem[] => {
  const workplaceItem = lowTokens
    ? {
        key: 'workplace' as const,
        label: 'Billing',
        href: '/billing',
        icon: CreditCard,
        matchers: ['/billing'],
      }
    : {
        key: 'workplace' as const,
        label: 'Workplace',
        href: '/workplace',
        icon: Briefcase,
        matchers: ['/workplace', '/pt-assessment', '/billing'],
      };

  return [
    {
      key: 'home',
      label: 'Home',
      href: '/dashboard',
      icon: Home,
      matchers: ['/dashboard', '/daily-report', '/general-report'],
    },
    {
      key: 'reports',
      label: 'Reports',
      href: '/weekly-report',
      icon: FileText,
      matchers: ['/weekly-report'],
    },
    {
      key: 'enhancement',
      label: 'Enhancement',
      href: '/enhancement',
      icon: Sparkles,
      matchers: ['/enhancement', '/chatbot'],
    },
    workplaceItem,
    {
      key: 'profile',
      label: 'Profile',
      href: '/profile',
      icon: User,
      matchers: ['/profile', '/settings', '/help'],
    },
  ];
};

export const PRIMARY_NAV_ITEMS: PrimaryNavItem[] = getPrimaryNavItems();

// Bottom navigation (mobile) - shows Reports, Enhancement, Billing, and Help
export const getBottomNavItems = (): PrimaryNavItem[] => [
  {
    key: 'home',
    label: 'Home',
    href: '/dashboard',
    icon: Home,
    matchers: ['/dashboard', '/daily-report', '/general-report'],
  },
  {
    key: 'reports',
    label: 'Reports',
    href: '/weekly-report',
    icon: FileText,
    matchers: ['/weekly-report'],
  },
  {
    key: 'enhancement',
    label: 'Enhancement',
    href: '/enhancement',
    icon: Sparkles,
    matchers: ['/enhancement', '/chatbot'],
  },
  {
    key: 'billing',
    label: 'Billing',
    href: '/billing',
    icon: CreditCard,
    matchers: ['/billing'],
  },
  {
    key: 'help',
    label: 'Help',
    href: '/help',
    icon: HelpCircle,
    matchers: ['/help'],
  },
];

export const BOTTOM_NAV_ITEMS: PrimaryNavItem[] = getBottomNavItems();

export const PROFILE_SECONDARY_LINKS = [
  { label: 'Settings', href: '/settings', icon: Settings },
  { label: 'Help Center', href: '/help', icon: HelpCircle },
  { label: 'Caluu+', href: 'https://caluu.kodin.co.tz/dashboard', icon: ExternalLink, external: true },
];

export interface PageTitle {
  title: string;
  subtitle: string;
}

const DEFAULT_PAGE_TITLE: PageTitle = {
  title: 'MAIPT',
  subtitle: 'Practical Training',
};

const PAGE_TITLES: { matchers: string[]; title: PageTitle }[] = [
  {
    matchers: ['/enhancement', '/chatbot'],
    title: { title: 'Enhancement', subtitle: 'AI-Powered Assistant' },
  },
];

// Routes that own the full viewport height and render their own in-page actions,
// so the mobile bottom tab bar would compete with the page content.
const FULL_SCREEN_PATHS: string[] = ['/enhancement', '/chatbot'];

export const shouldHideBottomNav = (pathname: string): boolean =>
  FULL_SCREEN_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export const getPageTitle = (pathname: string): PageTitle => {
  const match = PAGE_TITLES.find((entry) =>
    entry.matchers.some((matcher) => pathname === matcher || pathname.startsWith(`${matcher}/`)),
  );

  return match?.title ?? DEFAULT_PAGE_TITLE;
};

export const getActivePrimaryNav = (pathname: string, navItems?: PrimaryNavItem[]): PrimaryNavKey => {
  const itemsToUse = navItems || PRIMARY_NAV_ITEMS;
  const matched = itemsToUse.find((item) =>
    item.matchers.some((matcher) => pathname === matcher || pathname.startsWith(`${matcher}/`)),
  );

  return matched?.key ?? 'home';
};
